import type {
    AspectId,
    Background,
    DesignElement,
    DesignIdentity,
    ImageRef,
    Lang,
    LayoutId,
    Pattern,
    ShapeElement,
    Slide,
    SlideContent,
    TextElement,
    TextMeasure,
    ThemeStyle
} from '@/lib/design/types'

import { fontCss } from '@/lib/design/fonts'
import { getSize } from '@/lib/design/sizes'
import { slideStrings } from '@/lib/design/strings'
import { fitTextSize, layoutText } from '@/lib/design/text'
import { themeBackground } from '@/lib/design/themes'

export type LayoutContext = {
    style: ThemeStyle
    aspect: AspectId
    identity: DesignIdentity
    photo: ImageRef | null
    lang: Lang
    measure: TextMeasure
}

export type BuiltLayout = {
    background: Background
    pattern: Pattern
    elements: Array<DesignElement>
}

export const LAYOUTS: Array<{ id: LayoutId, label: string, description: string }> = [
    { id: 'cover', label: 'Cover', description: 'Hook and subtitle' },
    { id: 'point', label: 'Point', description: 'Number, title and text' },
    { id: 'list', label: 'List', description: 'Title and up to 5 items' },
    { id: 'quote', label: 'Quote', description: 'A big quote' },
    { id: 'stat', label: 'Big number', description: 'One striking number' },
    { id: 'cta', label: 'Closing', description: 'Ask for a save or follow' },
    { id: 'blank', label: 'Blank', description: 'Just your handle' }
]

// Roles whose text is worked out when the slide is drawn, from its position and the identity.
export const LIVE_ROLES = ['counter', 'handle', 'card-handle', 'display-name', 'avatar-initial', 'number']

const SIDE = 96
const CHROME_TOP = 72
const CHROME_SIZE = 30

const pad2 = (value: number): string => String(value).padStart(2, '0')

export const resolveLiveText = (element: TextElement, index: number, total: number, identity: DesignIdentity): string => {
    switch (element.role) {
        case 'counter': return `${pad2(index + 1)}/${pad2(total)}`
        case 'number': return pad2(Math.max(index, 1))
        case 'handle':
        case 'card-handle': return identity.handle ? `@${identity.handle.replace(/^@/, '')}` : ''
        case 'display-name': return identity.displayName || (identity.handle ? identity.handle.replace(/^@/, '') : '')
        case 'avatar-initial': {
            const source = identity.displayName || identity.handle.replace(/^@/, '') || 'F'

            return source.trim().charAt(0).toLocaleUpperCase()
        }
        default: return element.text
    }
}

type Colors = {
    text: string
    muted: string
    accentInk: string
}

const colorsFor = (style: ThemeStyle, onPhoto: boolean): Colors => {
    if (onPhoto) return { text: '#FFFFFF', muted: '#E5E7EB', accentInk: style.palette.accent }

    return {
        text: style.palette.text,
        muted: style.palette.muted,
        accentInk: style.highlightStyle === 'color' ? style.palette.accent : style.palette.text
    }
}

const textElement = (id: string, props: Partial<TextElement> & Pick<TextElement, 'x' | 'y' | 'width' | 'text' | 'font' | 'size' | 'color'>): TextElement => ({
    id,
    type: 'text',
    role: id,
    generated: true,
    rotation: 0,
    opacity: 1,
    weight: 400,
    italic: false,
    highlight: '#FF6B4A',
    highlightText: '#111111',
    highlightStyle: 'color',
    align: 'left',
    lineHeight: 1.3,
    letterSpacing: 0,
    uppercase: false,
    maxHeight: null,
    ...props
})

const shapeElement = (id: string, props: Partial<ShapeElement> & Pick<ShapeElement, 'shape' | 'x' | 'y' | 'width' | 'height'>): ShapeElement => ({
    id,
    type: 'shape',
    role: id,
    generated: true,
    rotation: 0,
    opacity: 1,
    fill: 'transparent',
    stroke: 'transparent',
    strokeWidth: 0,
    radius: 0,
    ...props
})

type Block = {
    height: number
    gap: number
    place: (y: number) => Array<DesignElement>
}

const textBlock = (element: TextElement, measure: TextMeasure, gap: number): Block | null => {
    if (!element.text.trim()) return null

    const size = element.maxHeight ? fitTextSize(element, element.text, measure, element.size) : element.size
    const sized = { ...element, size }
    const height = layoutText(sized, sized.text, measure).height

    return { height, gap, place: y => [{ ...sized, y }] }
}

const stackHeight = (blocks: Array<Block>): number => blocks.reduce((total, block, index) => total + block.height + (index < blocks.length - 1 ? block.gap : 0), 0)

const placeBlocks = (blocks: Array<Block>, top: number): Array<DesignElement> => {
    const elements: Array<DesignElement> = []
    let y = top

    for (const block of blocks) {
        elements.push(...block.place(y))
        y += block.height + block.gap
    }

    return elements
}

const compact = <T>(items: Array<T | null | undefined | false>): Array<T> => items.filter(Boolean) as Array<T>

export const buildLayout = (layout: LayoutId, content: SlideContent, ctx: LayoutContext): BuiltLayout => {
    const { style, measure } = ctx
    const { width: W, height: H } = getSize(ctx.aspect)
    const strings = slideStrings(ctx.lang)
    const scale = style.headingScale
    const isCard = style.frame === 'card'
    const onPhoto = layout === 'cover' && style.photoCover && Boolean(ctx.photo?.key || ctx.photo?.src)
    const colors = colorsFor(style, onPhoto)
    const contentX = style.frame === 'margin' ? 170 : SIDE
    const align = style.align
    const highlight = {
        highlight: style.palette.accent,
        highlightText: style.palette.accentText,
        highlightStyle: style.highlightStyle
    }

    const background: Background = onPhoto && ctx.photo
        ? { type: 'image', image: ctx.photo, overlay: '#05070D', overlayOpacity: 0.55, fallback: style.palette.background }
        : themeBackground(style)

    const pattern: Pattern = onPhoto ? { type: 'none', color: '#000000', opacity: 0 } : style.pattern

    // Card themes draw the slide inside a white card, everything else uses the full slide.
    const cardX = 72
    const cardPad = 64
    const boxX = isCard ? cardX + cardPad : contentX
    const boxW = isCard ? W - cardX * 2 - cardPad * 2 : W - contentX - SIDE
    const top = 170
    const bottom = H - 170
    const available = bottom - top - (isCard ? cardPad * 2 + 128 : 0)

    const heading = (id: string, text: string, size: number, maxShare: number, extra: Partial<TextElement> = {}): TextElement => textElement(id, {
        x: boxX,
        y: 0,
        width: boxW,
        text,
        font: style.fonts.heading,
        size: Math.round(size * scale),
        weight: style.headingWeight,
        color: colors.text,
        align,
        lineHeight: style.headingLineHeight,
        letterSpacing: style.headingTracking,
        uppercase: style.headingUppercase,
        maxHeight: Math.round(available * maxShare),
        minSize: Math.round(size * scale * 0.42),
        ...highlight,
        ...extra
    })

    const body = (id: string, text: string, size: number, extra: Partial<TextElement> = {}): TextElement => textElement(id, {
        x: boxX,
        y: 0,
        width: boxW,
        text,
        font: style.fonts.body,
        size,
        weight: style.bodyWeight,
        color: colors.text,
        align,
        lineHeight: 1.42,
        maxHeight: Math.round(available * 0.42),
        minSize: Math.round(size * 0.7),
        ...highlight,
        ...extra
    })

    let blocks: Array<Block> = []

    switch (layout) {
        case 'cover': {
            blocks = compact([
                content.kicker ? textBlock(body('kicker', content.kicker, 30, { weight: 700, uppercase: true, letterSpacing: 4, color: colors.accentInk, maxHeight: null, lineHeight: 1.2 }), measure, 36) : null,
                textBlock(heading('title', content.title || '', 104, 0.66), measure, 40),
                content.body ? textBlock(body('body', content.body, 40, { color: colors.muted, lineHeight: 1.35, maxHeight: 200 }), measure, 0) : null
            ])
            break
        }

        case 'point': {
            blocks = compact([
                isCard ? null : textBlock(textElement('number', {
                    x: boxX,
                    y: 0,
                    width: boxW,
                    text: '01',
                    font: style.fonts.heading,
                    size: Math.round(150 * Math.min(scale, 1.1)),
                    weight: style.headingWeight,
                    color: colors.accentInk,
                    align,
                    lineHeight: 1,
                    letterSpacing: style.headingTracking
                }), measure, 36),
                textBlock(heading('title', content.title || '', 74, 0.42), measure, 32),
                content.body ? textBlock(body('body', content.body, 38), measure, 0) : null
            ])
            break
        }

        case 'list': {
            const items = (content.items || []).filter(item => item.trim()).slice(0, 5)
            const itemBlocks: Array<Block> = []

            items.forEach((item, index) => {
                const itemText = body(`item-${index}`, item, 36, { x: boxX + 56, width: boxW - 56, align: 'left', maxHeight: 150, lineHeight: 1.36 })
                const block = textBlock(itemText, measure, 30)

                if (!block) return

                const firstLine = itemText.size * itemText.lineHeight

                itemBlocks.push({
                    ...block,
                    place: y => [
                        shapeElement(`item-dot-${index}`, { shape: 'circle', x: boxX + 6, y: y + firstLine / 2 - 9, width: 18, height: 18, fill: style.palette.accent }),
                        ...block.place(y)
                    ]
                })
            })

            blocks = compact([
                textBlock(heading('title', content.title || '', 64, 0.3, { align: 'left' }), measure, 48),
                ...itemBlocks
            ])
            break
        }

        case 'quote': {
            blocks = compact([
                textBlock(textElement('quote-mark', {
                    x: boxX,
                    y: 0,
                    width: boxW,
                    text: '“',
                    font: style.fonts.heading === 'anton' || style.fonts.heading === 'bebas' ? 'dm-serif' : style.fonts.heading,
                    size: 220,
                    weight: style.headingWeight,
                    color: colors.accentInk,
                    align,
                    lineHeight: 0.62
                }), measure, 24),
                textBlock(heading('title', content.title || '', 66, 0.55), measure, 36),
                content.body ? textBlock(body('body', content.body, 32, { color: colors.muted, weight: 600 }), measure, 0) : null
            ])
            break
        }

        case 'stat': {
            blocks = compact([
                textBlock(heading('stat', content.stat || '', 230, 0.32, { color: colors.accentInk, lineHeight: 1, uppercase: false }), measure, 28),
                textBlock(heading('title', content.title || '', 58, 0.28), measure, 28),
                content.body ? textBlock(body('body', content.body, 36), measure, 0) : null
            ])
            break
        }

        case 'cta': {
            const buttonText = content.button || ''
            const buttonFont = style.fonts.body
            const buttonSize = 34
            const buttonHeight = 100
            const buttonWidth = buttonText ? Math.min(boxW, Math.round(measure(buttonText, fontCss(buttonFont, buttonSize, 700, false), 0)) + 120) : 0
            const buttonX = align === 'center' ? boxX + (boxW - buttonWidth) / 2 : boxX

            blocks = compact([
                textBlock(heading('title', content.title || '', 90, 0.4), measure, 32),
                content.body ? textBlock(body('body', content.body, 38, { color: colors.muted }), measure, 56) : null,
                buttonText ? {
                    height: buttonHeight,
                    gap: 0,
                    place: (y: number) => [
                        shapeElement('button-bg', { shape: 'rect', x: buttonX, y, width: buttonWidth, height: buttonHeight, fill: style.palette.accent, radius: 50 }),
                        textElement('button', {
                            x: buttonX,
                            y: y + (buttonHeight - buttonSize * 1.2) / 2,
                            width: buttonWidth,
                            text: buttonText,
                            font: buttonFont,
                            size: buttonSize,
                            weight: 700,
                            color: style.palette.accentText,
                            align: 'center',
                            lineHeight: 1.2
                        })
                    ]
                } : null
            ])
            break
        }

        default:
            blocks = []
    }

    // Blocks never get taller than the room left, so slides never overflow.
    const totalHeight = stackHeight(blocks)
    const elements: Array<DesignElement> = []

    if (style.frame === 'border') {
        elements.push(shapeElement('frame', { shape: 'rect', x: 40, y: 40, width: W - 80, height: H - 80, stroke: colors.text, strokeWidth: 3, opacity: 0.16 }))
    }

    if (isCard) {
        const headerHeight = 88
        const cardHeight = cardPad * 2 + headerHeight + (blocks.length ? 40 + totalHeight : 0)
        const cardY = Math.max(140, Math.round((H - cardHeight) / 2))
        const avatarX = cardX + cardPad
        const avatarY = cardY + cardPad
        const logo = ctx.identity.logo

        elements.push(shapeElement('card', { shape: 'rect', x: cardX, y: cardY, width: W - cardX * 2, height: cardHeight, fill: style.palette.surface, radius: 40, shadow: true }))

        if (logo && (logo.key || logo.src)) {
            elements.push({ id: 'avatar', type: 'image', role: 'avatar', generated: true, rotation: 0, opacity: 1, x: avatarX, y: avatarY, width: headerHeight, height: headerHeight, image: logo, fit: 'cover', radius: headerHeight / 2 })
        }
        else {
            elements.push(shapeElement('avatar', { shape: 'circle', x: avatarX, y: avatarY, width: headerHeight, height: headerHeight, fill: style.palette.accent }))
            elements.push(textElement('avatar-initial', { x: avatarX, y: avatarY + (headerHeight - 44 * 1.2) / 2, width: headerHeight, text: 'F', font: style.fonts.heading, size: 44, weight: 700, color: style.palette.accentText, align: 'center', lineHeight: 1.2 }))
        }

        elements.push(textElement('display-name', { x: avatarX + headerHeight + 24, y: avatarY + 4, width: boxW - headerHeight - 24, text: 'Your name', font: style.fonts.heading, size: 34, weight: 700, color: style.palette.text, lineHeight: 1.2 }))
        elements.push(textElement('card-handle', { x: avatarX + headerHeight + 24, y: avatarY + 48, width: boxW - headerHeight - 24, text: '@handle', font: style.fonts.body, size: 28, weight: 400, color: style.palette.muted, lineHeight: 1.2 }))
        elements.push(...placeBlocks(blocks, avatarY + headerHeight + 40))
    }
    else {
        const placement = layout === 'cover' ? style.coverPlacement : 'center'
        const start = placement === 'bottom' ? bottom - totalHeight : top + Math.max(0, (bottom - top - totalHeight) / 2)

        elements.push(...placeBlocks(blocks, Math.round(start)))
    }

    // Chrome: handle top left, slide counter top right, swipe cue bottom right.
    if (!isCard) {
        elements.push(textElement('handle', { x: contentX, y: CHROME_TOP, width: 600, text: '@handle', font: style.fonts.body, size: CHROME_SIZE, weight: 600, color: colors.muted, lineHeight: 1.2 }))
    }

    elements.push(textElement('counter', { x: W - SIDE - 240, y: CHROME_TOP, width: 240, text: '01/08', font: style.fonts.body, size: CHROME_SIZE, weight: 600, color: colors.muted, align: 'right', lineHeight: 1.2, letterSpacing: 2 }))

    if (layout !== 'cta') {
        const swipeY = H - CHROME_TOP - 36

        elements.push(textElement('swipe', { x: W - SIDE - 340, y: swipeY, width: 260, text: strings.swipe, font: style.fonts.body, size: CHROME_SIZE, weight: 700, color: colors.accentInk, align: 'right', lineHeight: 1.2, hiddenOnLast: true }))
        elements.push(shapeElement('swipe-arrow', { shape: 'arrow', x: W - SIDE - 60, y: swipeY + 2, width: 60, height: 32, stroke: colors.accentInk, strokeWidth: 5, hiddenOnLast: true }))
    }

    return { background, pattern, elements }
}

export type RelayoutOptions = {
    keepOverrides: boolean
    keepBackground: boolean
}

// Rebuilds the template parts of a slide from its content. Anything the person added
// themselves stays untouched, and so do the properties they changed by hand.
export const relayoutSlide = (slide: Slide, ctx: LayoutContext, options: RelayoutOptions = { keepOverrides: true, keepBackground: true }): Slide => {
    const built = buildLayout(slide.layout, slide.content, ctx)
    const previous = new Map(slide.elements.filter(element => element.generated).map(element => [element.id, element]))

    const hidden = new Set(slide.hiddenRoles || [])

    const generated = built.elements.filter(element => !hidden.has(element.role || '')).map(element => {
        const before = previous.get(element.id)

        if (!options.keepOverrides || !before?.overrides?.length || before.type !== element.type) return element

        const merged: Record<string, unknown> = { ...element }
        const source = before as unknown as Record<string, unknown>

        for (const key of before.overrides) {
            if (key in source) merged[key] = source[key]
        }

        merged.overrides = before.overrides

        return merged as unknown as DesignElement
    })

    const custom = slide.elements.filter(element => !element.generated)
    const keepBackground = options.keepBackground && slide.customBackground

    return {
        ...slide,
        background: keepBackground ? slide.background : built.background,
        pattern: keepBackground ? slide.pattern : built.pattern,
        customBackground: keepBackground ? slide.customBackground : false,
        elements: [...generated, ...custom]
    }
}
